import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../App';
import axios from 'axios';
import { QRCodeSVG } from 'qrcode.react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || window.location.origin;
const API = `${BACKEND_URL}/api`;

// Helper to get current date in GMT+7
const getCurrentDateGMT7 = () => {
  const now = new Date();
  const gmt7 = new Date(now.getTime() + (7 * 60 * 60 * 1000));
  return gmt7;
};

const formatDateKey = (dateStr) => {
  const date = new Date(dateStr);
  const gmt7 = new Date(date.getTime() + (7 * 60 * 60 * 1000));
  return gmt7.toISOString().split('T')[0];
};

const BottomNavigation = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, token } = useAuth();
  const [showTicketCarousel, setShowTicketCarousel] = useState(false);
  const [upcomingTickets, setUpcomingTickets] = useState([]);
  const [currentTicketIndex, setCurrentTicketIndex] = useState(0);
  const [loading, setLoading] = useState(false);

  // Fetch upcoming tickets
  const fetchUpcomingTickets = useCallback(async () => {
    if (!token) return;
    
    setLoading(true);
    try {
      const response = await axios.get(`${API}/tickets/my-tickets`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      const tickets = response.data.data || [];
      const now = getCurrentDateGMT7();
      const todayStr = now.toISOString().split('T')[0];
      
      // Filter only upcoming tickets (jadwal >= today)
      const upcoming = tickets.filter(ticket => {
        const sessionDate = ticket.m_ticket_session?.tanggal_pertandingan;
        if (!sessionDate) return false;
        const ticketDateKey = formatDateKey(sessionDate);
        return ticketDateKey >= todayStr;
      });
      
      setUpcomingTickets(upcoming);
    } catch (error) {
      console.error('Error fetching tickets:', error);
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    if (showTicketCarousel && token) {
      fetchUpcomingTickets();
    }
  }, [showTicketCarousel, token, fetchUpcomingTickets]);

  // Don't show on admin panel, landing page, register, login, verify-email
  if (location.pathname.includes('/admin') || 
      location.pathname === '/' || 
      location.pathname === '/register' ||
      location.pathname === '/login' ||
      location.pathname === '/verify-email' ||
      location.pathname === '/forgot-password' ||
      location.pathname === '/reset-password') {
    return null;
  }

  // Only show for logged in users
  if (!user) {
    return null;
  }

  const isActive = (path) => {
    if (path === '/home') {
      return location.pathname === '/home';
    }
    return location.pathname.startsWith(path);
  };

  const handlePrevTicket = () => {
    setCurrentTicketIndex((prev) => 
      prev === 0 ? upcomingTickets.length - 1 : prev - 1
    );
  };

  const handleNextTicket = () => {
    setCurrentTicketIndex((prev) => 
      prev === upcomingTickets.length - 1 ? 0 : prev + 1
    );
  };

  const formatDateTime = (dateStr) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleString('id-ID', {
      weekday: 'short',
      day: 'numeric',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case 'active':
        return <span className="px-2 py-0.5 bg-green-100 text-green-800 rounded-full text-xs font-bold">Aktif</span>;
      case 'redeemed':
        return <span className="px-2 py-0.5 bg-blue-100 text-blue-800 rounded-full text-xs font-bold">Sudah Ditukar</span>;
      default:
        return <span className="px-2 py-0.5 bg-gray-100 text-gray-800 rounded-full text-xs font-bold">{status}</span>;
    }
  };

  const currentTicket = upcomingTickets[currentTicketIndex];

  return (
    <>
      {/* Ticket Carousel Modal */}
      {showTicketCarousel && (
        <div 
          className="fixed inset-0 bg-black/70 z-[60] flex items-end justify-center"
          onClick={() => setShowTicketCarousel(false)}
        >
          <div 
            className="bg-white rounded-t-3xl w-full max-w-lg max-h-[85vh] overflow-hidden animate-slide-up"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Header */}
            <div className="bg-gradient-to-r from-blue-500 to-blue-600 text-white p-4 flex justify-between items-center">
              <h3 className="font-bold text-lg">Tiket Saya</h3>
              <button 
                onClick={() => setShowTicketCarousel(false)}
                className="p-1 hover:bg-white/20 rounded-full"
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            {/* Content */}
            <div className="p-4">
              {loading ? (
                <div className="text-center py-12">
                  <div className="w-10 h-10 border-4 border-blue-500 border-t-transparent rounded-full animate-spin mx-auto mb-3"></div>
                  <p className="text-gray-500">Memuat tiket...</p>
                </div>
              ) : upcomingTickets.length === 0 ? (
                <div className="text-center py-12">
                  <svg className="w-16 h-16 mx-auto mb-4 text-gray-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 5v2m0 4v2m0 4v2M5 5a2 2 0 00-2 2v3a2 2 0 110 4v3a2 2 0 002 2h14a2 2 0 002-2v-3a2 2 0 110-4V7a2 2 0 00-2-2H5z" />
                  </svg>
                  <p className="text-gray-500 mb-4">Tidak ada tiket untuk jadwal yang akan datang</p>
                  <button
                    onClick={() => {
                      setShowTicketCarousel(false);
                      navigate('/tickets');
                    }}
                    className="bg-blue-500 hover:bg-blue-600 text-white px-6 py-2 rounded-lg font-semibold"
                  >
                    Beli Tiket
                  </button>
                </div>
              ) : (
                <>
                  {/* Carousel Navigation */}
                  <div className="flex items-center justify-between mb-4">
                    <button
                      onClick={handlePrevTicket}
                      disabled={upcomingTickets.length <= 1}
                      className="p-2 bg-gray-100 hover:bg-gray-200 rounded-full disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                      </svg>
                    </button>
                    <span className="text-sm text-gray-500">
                      {currentTicketIndex + 1} / {upcomingTickets.length}
                    </span>
                    <button
                      onClick={handleNextTicket}
                      disabled={upcomingTickets.length <= 1}
                      className="p-2 bg-gray-100 hover:bg-gray-200 rounded-full disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                      </svg>
                    </button>
                  </div>

                  {/* Ticket Card */}
                  {currentTicket && (
                    <div className="bg-gradient-to-br from-blue-50 to-white border-2 border-blue-200 rounded-2xl overflow-hidden">
                      {/* Ticket Header */}
                      <div className="bg-blue-500 text-white p-3 text-center">
                        <div className="text-xs opacity-80">KODE TIKET</div>
                        <div className="text-xl font-mono font-bold tracking-wider">{currentTicket.kode_tiket}</div>
                        <div className="mt-1">{getStatusBadge(currentTicket.status)}</div>
                      </div>

                      {/* QR Code */}
                      <div className="flex justify-center p-4 bg-white">
                        <div className="bg-white p-3 rounded-xl shadow-md">
                          <QRCodeSVG 
                            value={currentTicket.kode_tiket}
                            size={140}
                            level="H"
                          />
                        </div>
                      </div>

                      {/* Ticket Info */}
                      <div className="p-4 space-y-2">
                        <h4 className="font-bold text-gray-900">{currentTicket.m_ticket_session?.nama_sesi}</h4>
                        <div className="flex items-center gap-2 text-sm text-gray-600">
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                          </svg>
                          {formatDateTime(currentTicket.m_ticket_session?.tanggal_pertandingan)}
                        </div>
                        {currentTicket.m_ticket_session?.lokasi && (
                          <div className="flex items-center gap-2 text-sm text-gray-600">
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                            </svg>
                            {currentTicket.m_ticket_session.lokasi}
                          </div>
                        )}
                      </div>

                      {currentTicket.status === 'active' && (
                        <div className="bg-yellow-50 border-t border-yellow-200 p-3 text-center">
                          <p className="text-xs text-yellow-800">
                            Tunjukkan QR Code ini saat penukaran tiket fisik
                          </p>
                        </div>
                      )}
                    </div>
                  )}

                  {/* Swipe Indicator Dots */}
                  {upcomingTickets.length > 1 && (
                    <div className="flex justify-center mt-4 gap-2">
                      {upcomingTickets.map((_, index) => (
                        <button
                          key={index}
                          onClick={() => setCurrentTicketIndex(index)}
                          className={`w-2 h-2 rounded-full transition-all ${
                            currentTicketIndex === index ? 'bg-blue-500 w-4' : 'bg-gray-300'
                          }`}
                        />
                      ))}
                    </div>
                  )}

                  {/* View All Link */}
                  <button
                    onClick={() => {
                      setShowTicketCarousel(false);
                      navigate('/tickets');
                    }}
                    className="w-full mt-4 text-center text-blue-600 font-semibold py-2 hover:underline"
                  >
                    Lihat Semua Tiket →
                  </button>
                </>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Bottom Navigation */}
      <nav className="fixed bottom-0 left-0 right-0 bg-white border-t border-gray-200 shadow-lg z-50">
        <div className="max-w-7xl mx-auto px-4">
          <div className="grid grid-cols-3 gap-1">
            {/* Home */}
            <button
              onClick={() => navigate('/home')}
              className={`flex flex-col items-center py-3 ${
                isActive('/home') ? 'text-ltb-bright' : 'text-gray-600 hover:text-ltb-bright'
              }`}
            >
              <svg className="w-6 h-6 mb-1" fill="currentColor" viewBox="0 0 24 24">
                <path d="M10 20v-6h4v6h5v-8h3L12 3 2 12h3v8z" />
              </svg>
              <span className="text-xs font-semibold">Home</span>
            </button>

            {/* Tiket Saya */}
            <button
              onClick={() => setShowTicketCarousel(true)}
              className={`flex flex-col items-center py-3 relative ${
                showTicketCarousel ? 'text-ltb-bright' : 'text-gray-600 hover:text-ltb-bright'
              }`}
            >
              <svg className="w-6 h-6 mb-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 5v2m0 4v2m0 4v2M5 5a2 2 0 00-2 2v3a2 2 0 110 4v3a2 2 0 002 2h14a2 2 0 002-2v-3a2 2 0 110-4V7a2 2 0 00-2-2H5z" />
              </svg>
              <span className="text-xs font-semibold">Tiket Saya</span>
            </button>

            {/* Klasemen - hidden */}

            {/* Profil */}
            <button
              onClick={() => navigate('/profile')}
              className={`flex flex-col items-center py-3 ${
                isActive('/profile') ? 'text-ltb-bright' : 'text-gray-600 hover:text-ltb-bright'
              }`}
            >
              <svg className="w-6 h-6 mb-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
              </svg>
              <span className="text-xs font-semibold">Profil</span>
            </button>
          </div>
        </div>
      </nav>

      {/* Animation Styles */}
      <style>{`
        @keyframes slide-up {
          from {
            transform: translateY(100%);
          }
          to {
            transform: translateY(0);
          }
        }
        .animate-slide-up {
          animation: slide-up 0.3s ease-out;
        }
      `}</style>
    </>
  );
};

export default BottomNavigation;
